import { AutoCompleteOptions, useLookupTables } from "../../../../hooks/useLookupTables"
import { useEffect, useMemo, useState } from "react"
import { useAxios } from "../../../../hooks/useAxios"
import { Data, MenuState, SelectedSubmission, SubmissionsAuditStatistics, SubmissionsLogsFilters } from "./types"
import { diplomatAxios } from "../../../../services/diplomatAxios"
import { toast } from "react-toastify"

const API_URL = process.env.REACT_APP_MAIN_BACKEND_URL

export function useServiceSubmissionsLogs() {
  const [page, setPage] = useState(1)

  const { lookupTables, isLoadingLookupTables, errorLookupTables } =
    useLookupTables(['affiliations', 'terms'])

  const [filters, setFilters] = useState<SubmissionsLogsFilters>({
    affiliation: null,
    section: null,
    term: { id: 16, label: 'Fall 2025' },
    name: '',
    auditable: 0,
    page_blurred: 0,
    max_score: 0,
  })

  const [selectedRows, setSelectedRows] = useState<
    Map<number, SelectedSubmission>
  >(new Map())

  const sectionURL = useMemo(() => {
    const qs = []
    qs.push(`is_lookup=true`)

    // Unselect all rows
    setSelectedRows(new Map)

    if (filters.affiliation)
      qs.push(`fk_id_affiliation=${filters.affiliation.id}`)
    if (filters.term) qs.push(`fk_id_term=${filters.term.id}`)
    return `${API_URL}/sections?${qs.join('&')}`
  }, [filters])

  const {
    data: sections,
    error: errorSections,
    isLoading: isLoadingSections,
  } = useAxios<AutoCompleteOptions[]>(sectionURL)

  const submissionsURL = useMemo(() => {
    const qs = []

    qs.push(`page=${page}`)
    if (filters.name) qs.push(`name=${filters.name}`)
    if (filters.affiliation)
      qs.push(`fk_id_affiliation=${filters.affiliation.id}`)
    if (filters.term) qs.push(`fk_id_term=${filters.term.id}`)
    if (filters.section) qs.push(`fk_id_section=${filters.section.id}`)
    if (filters.auditable) qs.push(`auditable=${filters.auditable}`)
    if (filters.page_blurred) qs.push(`page_blurred=${filters.page_blurred}`)
    if (filters.max_score) qs.push(`max_score=${filters.max_score}`)
    return `${API_URL}/get-last-updated-submissions-logs?${qs.join('&')}`
  }, [filters, page])

  const { data, error, isLoading } = useAxios<Data>(
    submissionsURL,
    undefined,
    500
  )

  // If the filters change, reset the page
  useEffect(() => {
    setPage(1)
  }, [filters.affiliation, filters.section, filters.term, filters.name])

  const handleSelectRow = (row: SubmissionsAuditStatistics) => {
    setSelectedRows((prev) => {
      const updatedMap = new Map(prev)
      if (selectedRows.has(row.fk_id_submission)) {
        updatedMap.delete(row.fk_id_submission)
      } else {
        const regex = /\/courses\/(\d+)\/quizzes\/(\d+)/
        const match = row.assignment_link ? row.assignment_link.match(regex) : null

        if (!match) return updatedMap
        else
          updatedMap.set(row.fk_id_submission, {
            lms_user_id: row.submission.lms_cnv_user_id,
            lms_course_id: Number(match[1]),
            lms_quiz_id: Number(match[2]),
            attempt: row.attempt
          })
      }
      return updatedMap
    })
  }

  const handleSelectAll = () => {
    setSelectedRows((prev) => {

      const updatedMap = new Map<number, SelectedSubmission>()

      const selectableRows = data!.data.filter(sub => Boolean(sub.canceled_state) === false && sub.assignment_link)

      if (prev.size === selectableRows.length) {
        return updatedMap
      } else {
        selectableRows.forEach((row) => {
          const regex = /\/courses\/(\d+)\/quizzes\/(\d+)/
          const match = row.assignment_link ? row.assignment_link.match(regex) : null
          if (!match) console.log('Unable to get IDs from submission ' + row.fk_id_submission)
          else
            updatedMap.set(row.fk_id_submission, {
              lms_user_id: row.submission.lms_cnv_user_id,
              lms_course_id: Number(match[1]),
              lms_quiz_id: Number(match[2]),
              attempt: row.attempt
            })
        })
      }
      return updatedMap
    })
  }

  const [actionsMenu, setActionsMenu] = useState<MenuState>({
    anchorEl: null,
  })

  const handleCancelSubmissions = async () => {
    const _toast = toast('Cancelling submissions', { toastId: 'toast-cancel-submissions', isLoading: true })
    // Convert map into array
    const payload: (SelectedSubmission & { sis_submission_id: number })[] = []
    selectedRows.forEach((value, key) => {
      payload.push({
        sis_submission_id: key,
        lms_user_id: value.lms_user_id,
        lms_course_id: value.lms_course_id,
        lms_quiz_id: value.lms_quiz_id,
        attempt: value.attempt
      })
    })

    // Abort HTTP request if any submission has missing info
    const invalidObjects: any[] = []
    for (const submission of payload) {
      const values = Object.values(submission)
      if (values.some(value => Number.isNaN(value))) invalidObjects.push(submission)
    }
    if (Boolean(invalidObjects.length)) {
      console.log('Faulty selected objects:')
      for (const faultyObj of invalidObjects) { console.log(faultyObj) }
      return toast.update(_toast, { render: `Unable to send ${invalidObjects.length} selected row(s).`, isLoading: false, type: 'warning' })
    }

    // Send HTTP request to diplomat
    console.log('Sending submissions to cancel...', payload)
    try {
      const response = await diplomatAxios.put(`/cancel-submissions`, payload)
      toast.update(_toast, { render: response.data.message, isLoading: false, autoClose: 2000, type: 'success' })

      // const recordsToBeUpdated = []
      if (response.data.invalidParams.length <= 0) {
        // Handle 200, update data adding the 'canceled_state' to records that were successfully processed

      } else {
        return
      }
    } catch (error) {
      console.log(error)
      toast.update(_toast, { render: `Unable to cancel submissions.`, isLoading: false, type: 'error' })
    }
  }

  return {
    //STATES
    page,
    setPage,
    filters,
    setFilters,
    selectedRows,
    handleSelectRow,
    handleSelectAll,

    //MENUS
    actionsMenu,
    setActionsMenu,

    //DATA
    data,
    lookupTables,
    sections,

    //ERRORS
    error,
    errorLookupTables,
    errorSections,

    //LOADING
    isLoading,
    isLoadingSections,
    isLoadingLookupTables,

    // ACTIONS
    handleCancelSubmissions,
  }
}
